// src/utils/calculoRenta210.js
// Cálculo determinístico del impuesto de renta (Cédula General + Pensiones)
// basado en el Liquidador DRPN-AG-2025 v9.0 y los Art. 241 y 336 del E.T.
// La IA NUNCA calcula estos números — solo los explica después.

// UVT por año gravable. Actualizar cada año cuando la DIAN publique el nuevo valor.
const UVT_POR_ANIO = {
  2022: 38004,
  2023: 42412,
  2024: 47065,
  2025: 49799,
}

export function obtenerUVT(anioGravable) {
  return UVT_POR_ANIO[anioGravable] || UVT_POR_ANIO[2025]
}

// Tabla Art. 241 E.T. — Cédula General y Pensiones combinadas
// (rangos en UVT, tarifa marginal, UVT que se restan del cálculo)
const TABLA_ART_241 = [
  { desde: 0, hasta: 1090, tarifa: 0, restaUVT: 0, sumaUVT: 0 },
  { desde: 1090, hasta: 1700, tarifa: 0.19, restaUVT: 1090, sumaUVT: 0 },
  { desde: 1700, hasta: 4100, tarifa: 0.28, restaUVT: 1700, sumaUVT: 116 },
  { desde: 4100, hasta: 8670, tarifa: 0.33, restaUVT: 4100, sumaUVT: 788 },
  { desde: 8670, hasta: 18970, tarifa: 0.35, restaUVT: 8670, sumaUVT: 2296 },
  { desde: 18970, hasta: 31000, tarifa: 0.37, restaUVT: 18970, sumaUVT: 5901 },
  { desde: 31000, hasta: Infinity, tarifa: 0.39, restaUVT: 31000, sumaUVT: 10352 },
]

function aplicarTablaArt241(baseUVT) {
  if (baseUVT <= 0) return 0
  const tramo = TABLA_ART_241.find(t => baseUVT > t.desde && baseUVT <= t.hasta) || TABLA_ART_241[TABLA_ART_241.length - 1]
  return (baseUVT - tramo.restaUVT) * tramo.tarifa + tramo.sumaUVT
}

const n = (v) => Number(v) || 0

/**
 * Calcula el impuesto de renta a partir de las respuestas de la declaración.
 * Devuelve un desglose completo en pesos, transparente y auditable.
 */
export function calcularImpuestoRenta(declaracion) {
  const anio = declaracion.anio_gravable
  const UVT = obtenerUVT(anio)
  const r = declaracion.respuestas || {}
  const cedulas = declaracion.cedulas_seleccionadas || []

  // ── 1. Ingresos brutos por cédula ──────────────────────────────────────
  const ingresosTrabajo = n(r.ingresos_salarios)
  const ingresosHonorarios = cedulas.includes('honorarios') ? n(r.ingresos_honorarios) : 0
  const ingresosCapital = cedulas.includes('capital')
    ? n(r.ingresos_arrendamientos) + n(r.ingresos_intereses_financieros)
    : 0
  const ingresosNoLaboral = cedulas.includes('no_laboral') ? n(r.ingresos_negocio_propio) : 0
  const ingresosPension = cedulas.includes('pensiones') ? n(r.ingresos_pension) : 0

  const ingresosBrutosCedulaGeneral = ingresosTrabajo + ingresosHonorarios + ingresosCapital + ingresosNoLaboral

  // ── 2. Costos y gastos procedentes (no aplican a Trabajo) ──────────────
  const costosHonorarios = n(r.costos_gastos_honorarios)
  const costosCapital = n(r.costos_gastos_capital)
  const costosNoLaboral = n(r.costos_gastos_no_laboral)
  const totalCostosGastos = costosHonorarios + costosCapital + costosNoLaboral

  const rentaLiquidaAntesDeBeneficios = ingresosBrutosCedulaGeneral - totalCostosGastos

  // ── 3. Deducciones y rentas exentas (con sus topes individuales) ───────
  const dependientesTope = Math.min(
    n(r.num_dependientes) > 0 ? ingresosTrabajo * 0.10 : 0,
    384 * UVT
  )
  const interesesViviendaTope = Math.min(ingresosTrabajo > 0 ? n(r.intereses_vivienda) : 0, 1200 * UVT)
  const medicinaPrepagadaTope = Math.min(n(r.medicina_prepagada) + n(r.dependientes_deduccion_honorarios) || 0, 192 * UVT)
  const aportesVoluntariosPension = n(r.aportes_voluntarios_pension)
  const aportesAFC = n(r.aportes_afc)

  const totalBeneficiosSinLimite =
    dependientesTope + interesesViviendaTope + medicinaPrepagadaTope + aportesVoluntariosPension + aportesAFC

  // Límite general Art. 336 E.T.: el menor entre 40% de la renta líquida y 1.340 UVT anuales
  const limite40Pct = rentaLiquidaAntesDeBeneficios * 0.40
  const limite1340UVT = 1340 * UVT
  const topeBeneficios = Math.max(0, Math.min(limite40Pct, limite1340UVT))
  const beneficiosAplicados = Math.min(totalBeneficiosSinLimite, topeBeneficios)

  const rentaLiquidaCedulaGeneral = Math.max(0, rentaLiquidaAntesDeBeneficios - beneficiosAplicados)

  // ── 4. Cédula de Pensiones (tope de renta exenta: 12.000 UVT anuales) ──
  const exentoPension = Math.min(ingresosPension, 12000 * UVT)
  const rentaLiquidaPensiones = Math.max(0, ingresosPension - exentoPension)

  // ── 5. Base gravable total y aplicación de la tarifa ────────────────────
  const baseGravableTotal = rentaLiquidaCedulaGeneral + rentaLiquidaPensiones
  const baseGravableUVT = baseGravableTotal / UVT
  const impuestoUVT = aplicarTablaArt241(baseGravableUVT)
  const impuestoCalculado = Math.round(impuestoUVT * UVT)

  // ── 6. Retenciones (créditos contra el impuesto, no contra la base) ────
  const totalRetenciones =
    n(r.retenciones_en_la_fuente) +
    n(r.retenciones_honorarios) +
    n(r.retenciones_capital) +
    n(r.retenciones_no_laboral) +
    n(r.retenciones_pension)

  const saldoAPagarOFavor = impuestoCalculado - totalRetenciones

  return {
    anio,
    uvt: UVT,
    ingresosBrutosCedulaGeneral,
    ingresosPension,
    totalCostosGastos,
    rentaLiquidaAntesDeBeneficios,
    totalBeneficiosSinLimite,
    topeBeneficios,
    beneficiosAplicados,
    rentaLiquidaCedulaGeneral,
    exentoPension,
    rentaLiquidaPensiones,
    baseGravableTotal,
    baseGravableUVT,
    impuestoCalculado,
    totalRetenciones,
    saldoAPagarOFavor,
    esAFavor: saldoAPagarOFavor < 0,
  }
}